import { useLayoutAppKind } from "@ui/layouts/layoutProvider";
import { Panel } from "@ui/Panel";
import { HStack, VStack } from "@ui/Stack";
import {
	type Accessor,
	Match,
	Show,
	Switch,
	useContext,
} from "solid-js";
import { AddToBoardContext } from "~/modules/shared/boards/boardsContext";
import { UpdateBoardItemButton } from "../UpdateBoardItemButton";
import "./MoveBoardPanel.css";
import { Button } from "@ui/Button";
import { Icon } from "@ui/Icon";
import { PanelFooter } from "@ui/PanelFooter";
import { AddBoardForm } from "~/modules/askgvt/boards/list/components/AddBoardForm";
import { BoardList } from "~/modules/shared/boards/boardList/BoardList";
import { NewBoardButton } from "~/modules/shared/boards/NewBoardButton";
import alertTriangleIcon from "~/modules/shared/gvtui/icons/alertTriangle.svg?raw";
import { RemoveFromBoardButton } from "../RemoveFromBoardButton";
import { AddToBoardButton } from "./AddToBoardButton";
import { VideoPreviewWithScrubber } from "./VideoPreviewWithScrubber";

type Props = {
	isOpen: Accessor<boolean>;
	setIsOpen: (isOpen: boolean) => void;
	projectId?: string;
	refetch?: (clipIds: string[]) => void;
};
export const MoveBoardPanel = (props: Props) => {
	const appKind = useLayoutAppKind();
	const showPrivacy = appKind === "askgvt" || appKind === "studio";
	const { isOpen, setIsOpen } = props;
	const [
		store,
		{ clearBoard, setSelectedBoardId, setCurrentPanel, setFormData },
	] = useContext(AddToBoardContext);
	const panelTitle = () => {
		if (store.currentPanel() === 1) {
			return "New board";
		}
		return store.board?.id ? "Update board" : "Add clip to board";
	};
	return (
		<Panel
			onToggle={() => {
				setIsOpen(!isOpen());
			}}
			isOpen={isOpen}
			title={panelTitle()}
			symbol="content_cut"
			class="update-board-panel"
			footer={
				<Switch>
					<Match when={store.currentPanel() === 0}>
						<PanelFooter>
							<NewBoardButton onClick={() => setCurrentPanel(1)} />
							<UpdateBoardItemButton
								onClose={() => setIsOpen(!isOpen())}
								disabled={!store.selectedBoardId}
							/>
						</PanelFooter>
					</Match>
					<Match when={store.currentPanel() === 1}>
						<PanelFooter>
							<Button
								type="button"
								variant="secondary"
								theme="monochrome"
								onClick={() => setCurrentPanel(0)}
							>
								Cancel
							</Button>
							<AddToBoardButton
								onClose={() => setIsOpen(false)}
								projectId={props.projectId}
								refetch={props.refetch}
							/>
						</PanelFooter>
					</Match>
				</Switch>
			}
		>
			<Switch>
				<Match when={store.currentPanel() === 0}>
					<div
						style={{
							display: "grid",
							"grid-template-columns": "1fr",
							"grid-gap": "1rem",
						}}
					>
						<VideoPreviewWithScrubber />
						<VStack spacing="large">
							<Show when={store.board.id}>
								<div class="remove-from-board-button-container">
									<HStack spacing="small">
										<Icon
											class="alert-triangle-icon"
											icon={alertTriangleIcon}
										/>
										Clip is already in a board
									</HStack>
									<RemoveFromBoardButton
										onClose={() => {
											const clipId = store.clip?.id;
											if (clipId) {
												props.refetch?.([clipId]);
											}
											setIsOpen(false);
											clearBoard();
											setSelectedBoardId(null);
										}}
										boardId={store.board.id}
										clipId={store.clip?.id}
									/>
								</div>
							</Show>
							<BoardList
								boards={store.boardsList}
								selectedBoardId={() => store.selectedBoardId}
								onSelectBoard={setSelectedBoardId}
							/>
						</VStack>
					</div>
				</Match>
				<Match when={store.currentPanel() === 1}>
					<AddBoardForm
						errors={store.errors}
						formData={store.formData}
						setFormData={setFormData}
						showPrivacy={showPrivacy}
					/>
				</Match>
			</Switch>
		</Panel>
	);
};
